import { last } from 'lodash';
import { DateTime, Interval } from 'luxon';
import { interval, map, Observable, publishReplay, refCount } from 'rxjs';

import { applicationConfig, RoundConfig } from '../../applicationConfig';
import { sigUsdAsset } from '../assets/sigUsdAsset';
import { spfAsset } from '../assets/spfAsset';
import { Ratio } from '../models/Ratio';
import { useObservable } from './useObservable';

export interface Round extends Omit<RoundConfig, 'tokenPrice'> {
  readonly whitelistingInterval: Interval;
  readonly contributionInterval: Interval;
  readonly interval: Interval;
  readonly passed: boolean;
  readonly tokenPrice: Ratio;
}

export interface CurrentRound {
  readonly activeSubround: 'whitelist' | 'contribution';
}

export interface IdoInfo {
  readonly now: DateTime;
  readonly rounds: Round[];
  readonly currentRound?: Round & CurrentRound;
  readonly ended: boolean;
  readonly started: boolean;
}

const getIdoInfo = (now: DateTime): IdoInfo => {
  const rounds = applicationConfig.rounds.reduce<Round[]>((acc, r) => {
    const whitelistingDuration = Math.floor(
      r.contributionDuration
        ? r.duration - r.contributionDuration
        : r.duration / 2,
    );

    const startDateTime = last(acc)?.interval.end || applicationConfig.idoStart;

    const whitelistingInterval = Interval.fromDateTimes(
      startDateTime,
      startDateTime.plus({ hour: whitelistingDuration }),
    );
    const contributionInterval = Interval.fromDateTimes(
      startDateTime.plus({
        hour: whitelistingDuration,
      }),
      startDateTime.plus({ hour: r.duration }),
    );
    const interval = Interval.fromDateTimes(
      startDateTime,
      startDateTime.plus({ hour: r.duration }),
    );

    return acc.concat({
      ...r,
      tokenPrice: new Ratio(r.tokenPrice.toString(), sigUsdAsset, spfAsset),
      whitelistingInterval,
      contributionInterval,
      interval,
      passed: interval.end < now,
    });
  }, []);

  if (rounds.every((r) => r.passed)) {
    return {
      rounds,
      ended: true,
      started: true,
      currentRound: undefined,
      now,
    };
  }
  if (now < applicationConfig.idoStart) {
    return {
      rounds,
      ended: false,
      started: false,
      currentRound: undefined,
      now,
    };
  }

  const currentRound: Round | undefined = rounds.find((r) =>
    r.interval.contains(now),
  )!;
  return {
    now,
    rounds,
    currentRound: {
      ...currentRound,
      activeSubround: currentRound.whitelistingInterval.contains(now)
        ? 'whitelist'
        : 'contribution',
    },
    ended: false,
    started: true,
  };
};

export const idoInfo$: Observable<IdoInfo> = interval(
  applicationConfig.updateTime,
).pipe(
  map(() => getIdoInfo(DateTime.now())),
  publishReplay(1),
  refCount(),
);

export const useIdoInfo = (): IdoInfo => {
  const [lbaInfo] = useObservable(
    idoInfo$,
    [],
    getIdoInfo(DateTime.now().toUTC()),
  );

  return lbaInfo;
};
